import { z } from "zod";
import { api } from "../helpers/apiClient";

// ============================================================================
// SCHEMAS
// ============================================================================

export const TrialSummarySchema = z.object({
  trial_name: z.string(),
  task_id: z.string(),
  is_resolved: z.boolean().nullable().optional(),
  failure_mode: z.string(),
  input_tokens: z.number().nullable().optional(),
  output_tokens: z.number().nullable().optional(),
  runtime_ms: z.number().nullable().optional(),
  cost_usd: z.number().nullable().optional(),
  tests_passed: z.number(),
  tests_total: z.number(),
  langfuse_trace_url: z.string().nullable().optional(),
});

export const TrialResultSchema = z.object({
  trial_name: z.string(),
  task_id: z.string(),
  task_prompt: z.string(),
  is_resolved: z.boolean().nullable().optional(),
  failure_mode: z.string(),
  parser_results: z.record(z.string()).nullable().optional(),
  expected_test_count: z.number().nullable().optional(),
  input_tokens: z.number().nullable().optional(),
  output_tokens: z.number().nullable().optional(),
  cache_tokens: z.number().nullable().optional(),
  num_turns: z.number().nullable().optional(),
  runtime_ms: z.number().nullable().optional(),
  cost_usd: z.number().nullable().optional(),
  uuid: z.string().nullable().optional(),
  recording_path: z.string().nullable().optional(),
  langfuse_trace_url: z.string().nullable().optional(),
  agent: z.string().nullable().optional(),
  model_name: z.string().nullable().optional(),
  db_type: z.string().nullable().optional(),
  project_type: z.string().nullable().optional(),
  used_mcp: z.boolean().nullable().optional(),
});

export const RunMetadataSchema = z.object({
  run_id: z.string(),
  uuid: z.string().nullable().optional(),
  dataset_path: z.string().nullable().optional(),
  output_path: z.string().nullable().optional(),
  agent_name: z.string(),
  model_name: z.string().nullable().optional(),
  dataset_size: z.number(),
  n_attempts: z.number(),
  n_concurrent_trials: z.number(),
  accuracy: z.number().nullable().optional(),
  pass_at_k: z.record(z.number().nullable()).nullable().optional(),
  start_time: z.string().nullable().optional(),
  end_time: z.string().nullable().optional(),
  commit_hash: z.string(),
  username: z.string(),
  n_resolved: z.number().nullable().optional(),
  n_unresolved: z.number().nullable().optional(),
  tasks_attempted: z.number().nullable().optional(),
  total_cost_usd: z.number().nullable().optional(),
  total_tokens: z.number().nullable().optional(),
});

export const RunListResponseSchema = z.object({
  runs: z.array(RunMetadataSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  has_more: z.boolean(),
});

export const RunResultsResponseSchema = z.object({
  run_id: z.string(),
  results: z.array(TrialResultSchema),
  pass_at_k: z.record(z.number().nullable()).nullable().optional(),
  n_resolved: z.number(),
  n_unresolved: z.number(),
  accuracy: z.number(),
  resolved_ids: z.array(z.string()),
  unresolved_ids: z.array(z.string()),
});

export const TrialListResponseSchema = z.object({
  run_id: z.string(),
  trials: z.array(TrialSummarySchema),
  total: z.number(),
});

export const FilterOptionsResponseSchema = z.object({
  agents: z.array(z.string()),
  models: z.array(z.string()),
});

// --- Task Progress Schemas ---

export const TaskStatusEnum = z.enum(["solved", "partial", "failing"]);

export const TaskProgressItemSchema = z.object({
  task_id: z.string(),
  status: TaskStatusEnum,
  tests_passing: z.number(),
  tests_total: z.number(),
  attempts: z.number(),
  last_failure_mode: z.string().nullable().optional(),
  last_run_id: z.string().nullable().optional(),
  last_run_time: z.string().nullable().optional(),
  first_solved_run_id: z.string().nullable().optional(),
  langfuse_trace_url: z.string().nullable().optional(),
  best_input_tokens: z.number().nullable().optional(),
  best_output_tokens: z.number().nullable().optional(),
  best_cost_usd: z.number().nullable().optional(),
});

export const TaskProgressResponseSchema = z.object({
  tasks_solved: z.number(),
  tasks_total: z.number(),
  tasks_solved_pct: z.number(),
  tests_passing: z.number(),
  tests_total: z.number(),
  tests_passing_pct: z.number(),
  tasks: z.array(TaskProgressItemSchema),
});

// ============================================================================
// INFERRED TYPES
// ============================================================================

export type TrialSummary = z.infer<typeof TrialSummarySchema>;
export type TrialResult = z.infer<typeof TrialResultSchema>;
export type RunMetadata = z.infer<typeof RunMetadataSchema>;
export type RunListResponse = z.infer<typeof RunListResponseSchema>;
export type RunResultsResponse = z.infer<typeof RunResultsResponseSchema>;
export type TrialListResponse = z.infer<typeof TrialListResponseSchema>;
export type FilterOptionsResponse = z.infer<typeof FilterOptionsResponseSchema>;
export type TaskStatus = z.infer<typeof TaskStatusEnum>;
export type TaskProgressItem = z.infer<typeof TaskProgressItemSchema>;
export type TaskProgressResponse = z.infer<typeof TaskProgressResponseSchema>;

// ============================================================================
// API FUNCTIONS
// ============================================================================

export const getAdeBenchRuns = async (params?: {
  limit?: number;
  offset?: number;
  agent?: string;
  model?: string;
  start_date?: string;
  end_date?: string;
}): Promise<RunListResponse> => {
  const queryParams: Record<string, string | number> = {};
  if (params?.limit) queryParams.limit = params.limit;
  if (params?.offset) queryParams.offset = params.offset;
  if (params?.agent) queryParams.agent = params.agent;
  if (params?.model) queryParams.model = params.model;
  if (params?.start_date) queryParams.start_date = params.start_date;
  if (params?.end_date) queryParams.end_date = params.end_date;

  const result = await api.get<RunListResponse>(
    "/api/ade-bench/runs",
    queryParams,
    0,
    RunListResponseSchema
  );

  if (!result) {
    throw new Error("Failed to fetch ADE-Bench runs");
  }

  return result;
};

export const getAdeBenchRun = async (runId: string): Promise<RunMetadata> => {
  const result = await api.get<RunMetadata>(
    `/api/ade-bench/runs/${runId}`,
    {},
    0,
    RunMetadataSchema
  );

  if (!result) {
    throw new Error(`Failed to fetch ADE-Bench run ${runId}`);
  }

  return result;
};

export const getAdeBenchRunResults = async (runId: string): Promise<RunResultsResponse> => {
  const result = await api.get<RunResultsResponse>(
    `/api/ade-bench/runs/${runId}/results`,
    {},
    0,
    RunResultsResponseSchema
  );

  if (!result) {
    throw new Error(`Failed to fetch ADE-Bench run results for ${runId}`);
  }

  return result;
};

export const getAdeBenchTrials = async (runId: string): Promise<TrialListResponse> => {
  const result = await api.get<TrialListResponse>(
    `/api/ade-bench/runs/${runId}/trials`,
    {},
    0,
    TrialListResponseSchema
  );

  if (!result) {
    throw new Error(`Failed to fetch ADE-Bench trials for ${runId}`);
  }

  return result;
};

export const getAdeBenchTrial = async (runId: string, trialName: string): Promise<TrialResult> => {
  const result = await api.get<TrialResult>(
    `/api/ade-bench/runs/${runId}/trials/${trialName}`,
    {},
    0,
    TrialResultSchema
  );

  if (!result) {
    throw new Error(`Failed to fetch ADE-Bench trial ${trialName}`);
  }

  return result;
};

export const getAdeBenchFilterOptions = async (): Promise<FilterOptionsResponse> => {
  const result = await api.get<FilterOptionsResponse>(
    "/api/ade-bench/filters",
    {},
    0,
    FilterOptionsResponseSchema
  );

  if (!result) {
    throw new Error("Failed to fetch ADE-Bench filter options");
  }

  return result;
};

export const getAdeBenchTaskProgress = async (params?: {
  run_ids?: string[];
}): Promise<TaskProgressResponse> => {
  const queryParams: Record<string, string> = {};
  if (params?.run_ids && params.run_ids.length > 0) {
    queryParams.run_ids = params.run_ids.join(",");
  }

  const result = await api.get<TaskProgressResponse>(
    "/api/ade-bench/progress",
    queryParams,
    0,
    TaskProgressResponseSchema
  );

  if (!result) {
    throw new Error("Failed to fetch ADE-Bench task progress");
  }

  return result;
};

export const deleteAdeBenchRun = async (runId: string): Promise<{ message: string }> => {
  const result = await api.delete<{ message: string }>(`/api/ade-bench/runs/${runId}`, {});

  if (!result) {
    throw new Error(`Failed to delete ADE-Bench run ${runId}`);
  }

  return result;
};
